import { useEffect, useState } from "react"

import { Flex, Form, Input, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"

import ButtonComponent from "../../../Components/Button"
import InputComponent from "../../../Components/Input"
import { useIncognito } from "../../../hooks/useIncognito"
import Icon from "../../../Icon"

const { Text } = Typography

function ChangeEmailForm({
  userError,
  saveResult,
  userSaveResultReset,
  onClose,
  onSubmit,
  userErrorsReset,
  onChangeModal,
  onSaveConfirm,
  setField,
  email,
  isMobile
}) {
  const { t } = useTranslation()
  const incognito = useIncognito()

  const [errorText, setErrorText] = useState(null)
  const [currentError, setCurrentError] = useState({
    text: null,
    error: false
  })

  const [values, setValues] = useState({ current: null, email: null })

  const [form] = Form.useForm()

  useEffect(() => {
    if (userError) {
      if (typeof userError === "string") {
        if (userError.length > 100) {
          setErrorText(t("Server response error"))
        } else {
          setErrorText(userError)
        }
      } else {
        if (userError?.detail === t("Current password is entered incorrectly")) {
          setCurrentError({
            text: userError?.detail,
            error: true
          })
        } else {
          setErrorText(Object.values(userError)[0])
        }
      }
    }
  }, [userError])

  useEffect(() => {
    if (saveResult?.confirm) {
      onChangeModal("checkEmail")
      onSaveConfirm(values)
      userSaveResultReset()
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [saveResult])

  const onFinish = (value) => {
    const data = { ...value }
    userErrorsReset()
    onSubmit(data.current, data.email)
  }
  const handleClose = () => {
    clearFormData()
    onClose()
  }

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields) {
      setErrorText(errorFields[0].errors)
    }
  }

  function clearFormData() {
    form.resetFields()
    setValues({ current: null, email: null })
    setErrorText(null)
    setCurrentError({ text: null, error: false })
    userErrorsReset()
    userSaveResultReset()
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    setErrorText(null)
  }

  function onFocusHandler(e) {
    handleClearError(e, e.target.name)
  }

  function handleChange(e) {
    const { name, value } = e.target
    if (name === "current") {
      userErrorsReset()
      setCurrentError({ text: null, error: false })
    }

    setValues({ ...values, [name]: value })
  }

  function forgotPassword() {
    userErrorsReset()
    setField("")
    onChangeModal("forgotPassword")
  }

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
      requiredMark={true}
      initialValues={{
        email
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Form.Item
          label={t("Current password")}
          name="current"
          rules={[{ required: true, message: t("Required field") }]}
          validateStatus={currentError.error ? "error" : null}
          style={{ marginBottom: 0 }}
        >
          <Input.Password
            name="current"
            placeholder={t("Password")}
            autoComplete="current-password"
            visibilityToggle
            onFocus={onFocusHandler}
            onChange={handleChange}
          />
        </Form.Item>
        {currentError.text && (
          <Space>
            <Icon type={"alert_triangle_error"} />
            <Text type="danger">{currentError.text}</Text>
            <ButtonComponent
              type="link"
              onClick={forgotPassword}
              style={{ padding: 0, fontWeight: 400 }}
            >
              {t("Forgot password?")}
            </ButtonComponent>
          </Space>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Form.Item
          label={t("New email address")}
          name="email"
          rules={[{ required: true, message: t("Required field") }]}
          validateStatus={errorText ? "error" : null}
          style={{ marginBottom: 0 }}
        >
          <InputComponent
            name="email"
            type={incognito ? "password" : "email"}
            placeholder={t("New Email")}
            autoComplete="dontshow"
            onFocus={onFocusHandler}
            onChange={handleChange}
          />
        </Form.Item>
        {errorText ? (
          <Space>
            <Icon type={"alert_triangle_error"} />
            <Text type="danger">
              {errorText === "Enter a valid email address."
                ? t("Email address you entered is incorrect")
                : errorText}
            </Text>
          </Space>
        ) : null}
      </div>

      <Flex
        gap={16}
        justify="end"
        style={{
          padding: "0 3px 3px 0",
          flexDirection: isMobile && "column",
          paddingBottom: isMobile && 12
        }}
      >
        <ButtonComponent
          size="large"
          htmlType="button"
          onClick={handleClose}
          style={{ width: isMobile && "100%" }}
        >
          {t("Cancel")}
        </ButtonComponent>

        <ButtonComponent
          type="primary"
          size="large"
          htmlType="submit"
          disabled={!values.email || !values.current}
          style={{ width: isMobile && "100%" }}
        >
          {t("Continue")}
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default ChangeEmailForm
